import { useCallback, useMemo, useState } from "react";
import { CalendarCheck, Calendar, ClipboardList, FolderOpen, Phone, Sparkles } from "lucide-react";
import { resolverQuadros, type ChaveDoQuadro } from "@bookdental/schemas";
import { CoroaDentariaIcon } from "@/components/icons/CoroaDentariaIcon";
import { useAuth } from "@/lib/auth";
import { usePermissao } from "@/lib/use-permissao";
import { useFeature } from "@/lib/use-feature";
import { trpc } from "@/lib/trpc";

/** Nome e ícone de cada quadro — o que aparece no "Trocar quadro". */
export const QUADROS: Record<ChaveDoQuadro, { titulo: string; icone: React.ComponentType<{ className?: string }>; emBreve?: boolean }> = {
  consultas: { titulo: "Consultas agendadas", icone: Calendar },
  proteses: { titulo: "Pedidos de prótese", icone: CoroaDentariaIcon },
  tratamentos: { titulo: "Tratamentos em andamento", icone: ClipboardList },
  alertas: { titulo: "Alertas clínicos", icone: Sparkles, emBreve: true },
  atendimentos: { titulo: "Últimos atendimentos", icone: CalendarCheck },
  arquivos: { titulo: "Arquivos recentes", icone: FolderOpen },
  contato: { titulo: "Contato rápido", icone: Phone },
};

/**
 * Os quadros da aba Início da ficha, na preferência da pessoa (salva na conta, igual pra todo
 * paciente). Quadro que a pessoa não pode ver não entra nem na tela nem no "Trocar". O rascunho
 * local mostra a troca na hora, antes do servidor responder — mesmo desenho do menu reordenável.
 */
export function useQuadrosDaFicha() {
  const { user } = useAuth();
  const utils = trpc.useUtils();
  const [rascunho, setRascunho] = useState<ChaveDoQuadro[] | null>(null);
  const salvar = trpc.me.setQuadrosDaFicha.useMutation({ onSuccess: () => utils.me.current.invalidate() });

  const agenda = usePermissao("agenda.view");
  const protese = usePermissao("prosthesis.view");
  const podeArquivos = usePermissao("files.manage");
  const temArquivos = useFeature("patient.files");
  const arquivos = podeArquivos && temArquivos;
  const tratamentos = useFeature("patient.treatmentPlans");

  const disponiveis = useMemo(() => {
    const pode: Record<ChaveDoQuadro, boolean> = {
      consultas: agenda,
      proteses: protese,
      tratamentos,
      alertas: true,
      atendimentos: agenda,
      arquivos,
      contato: true,
    };
    return (Object.keys(QUADROS) as ChaveDoQuadro[]).filter((c) => pode[c]);
  }, [agenda, protese, arquivos, tratamentos]);

  const naTela = useMemo(
    () => resolverQuadros(rascunho ?? user?.quadrosDaFicha ?? null, disponiveis),
    [rascunho, user?.quadrosDaFicha, disponiveis],
  );

  const trocar = useCallback(
    (posicao: number, nova: ChaveDoQuadro) => {
      const lista = [...naTela];
      lista[posicao] = nova;
      setRascunho(lista);
      // Só grava com as quatro posições preenchidas — conta com menos quadros disponíveis que
      // posições fica no padrão calculado, sem preferência inválida no banco.
      if (lista.length === 4) salvar.mutate(lista);
    },
    [naTela, salvar],
  );

  // Rascunho vazio = padrão, já na tela enquanto o servidor não confirma.
  const restaurarPadrao = useCallback(() => {
    setRascunho([]);
    salvar.mutate(null);
  }, [salvar]);

  const personalizado = rascunho ? rascunho.length > 0 : !!user?.quadrosDaFicha?.length;

  return { naTela, disponiveis, trocar, restaurarPadrao, personalizado };
}
